/**
 * Framer Motion `layoutId` values shared between the Scanning and Brand Card
 * steps. Matching IDs across screens make the elements (favicon, name, color
 * swatches, tone chips) animate from their reveal positions on Scanning into
 * their final slots on the Brand Card — the "data lands in its home" moment.
 */
export const SHARED_LAYOUT_LOGO = 'onboarding-brand-logo'
export const SHARED_LAYOUT_NAME = 'onboarding-brand-name'
export const SHARED_LAYOUT_COLOR = (key: 'primary' | 'secondary' | 'accent'): string =>
  `onboarding-brand-color-${key}`
export const SHARED_LAYOUT_TONE = (tone: string): string => `onboarding-brand-tone-${tone}`

/**
 * Shared layout transition. The default ~300ms spring is too snappy for the
 * "wow" moment — at 700ms with a gentle out-cubic the user can actually see
 * each element travel from the scanning screen into its slot on the card.
 */
export const SHARED_LAYOUT_TRANSITION = {
  duration: 0.7,
  ease: [0.22, 0.9, 0.3, 1] as [number, number, number, number],
}

/**
 * Stagger delays for the shared elements so they settle in sequence, not all
 * at once. Logo arrives first (the anchor); each subsequent element waits 80ms
 * after the previous so the eye can track them landing one by one.
 */
const STAGGER_STEP_S = 0.08
export const layoutDelayForIndex = (i: number): number => i * STAGGER_STEP_S

/**
 * Pre-baked transition objects per index so framer-motion doesn't see a fresh
 * object identity on every render. Indexes match the visual reveal order:
 *   0 logo · 1 name · 2 color primary · 3 secondary · 4 accent · 5 tones
 */
export const SHARED_LAYOUT_TRANSITIONS: ReadonlyArray<
  typeof SHARED_LAYOUT_TRANSITION & { delay: number }
> = Array.from({ length: 6 }, (_, i) => ({
  ...SHARED_LAYOUT_TRANSITION,
  delay: layoutDelayForIndex(i),
}))

/** Convenience accessors so callers don't depend on the index numbering. */
export const TRANSITION_LOGO = SHARED_LAYOUT_TRANSITIONS[0]
export const TRANSITION_NAME = SHARED_LAYOUT_TRANSITIONS[1]
export const TRANSITION_COLOR = (k: 'primary' | 'secondary' | 'accent') =>
  SHARED_LAYOUT_TRANSITIONS[k === 'primary' ? 2 : k === 'secondary' ? 3 : 4]
export const TRANSITION_TONE = SHARED_LAYOUT_TRANSITIONS[5]
